import { ConsultantBookingStatus } from "@bonliva-crm/shared";
import { TextField, useSnackbarsPipeline } from "@bonliva-crm/ui";
import { LeadStatusModal } from "@consultants/consultant-profile-new/lead-status/lead-status-modal";
import { Box, Tooltip } from "@material-ui/core";
import { useNavigation } from "@navigation";
import { useEffect, useState } from "react";
import { useAddConsultantEventLogCall } from "../../hooks/add-consultant-event-log-call";
import { useAddConsultantEventLogComment } from "../../hooks/add-consultant-event-log-comment";
import { EventLogInputType, EventLogTextType } from "../../utils/event-log-input-type";
import { InputButtons } from "./components/input-buttons";
import {
  ConsultantLeadStatus,
  useGetPersonalInfoCardDataQuery
} from "@bonliva-crm/graphql";
import { useEventLogData } from "../../utils/refetch-consultant-events";
import { EventType } from "../../utils/hook-props";
import { useLocale } from "@hooks";

type Props = {
  consultantId: string;
  activeInput: EventLogInputType | null;
  setActiveInput: (input: EventLogInputType | null) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  inputDisabled?: boolean;
  bookingStatus?: ConsultantBookingStatus;
};

export const EventLogEntry = (props: Props) => {
  const [inputText, setInputText] = useState("");
  const { showError, showSuccess } = useSnackbarsPipeline();
  const nav = useNavigation();
  const { locale } = useLocale();
  const eventLogData = useEventLogData();
  const [leadStatusModalOpen, setLeadStatusModalOpen] = useState(false);

  useEffect(() => {
    !props.activeInput && setInputText("");
  }, [props.activeInput]);

  const onError = () => {
    showError(locale.cannotPerformAction);
    props.setLoading(false);
  };

  const onCompleted = (event?: EventType) => {
    props.setActiveInput(null);
    setInputText("");
    showSuccess(locale.eventAddedToEventLog);
    props.setLoading(false);
    eventLogData.refetchWithPolling(event?._id);
  };

  const onBeforeCall = () => {
    props.setLoading(true);
  };

  const { addConsultantEventLogComment } = useAddConsultantEventLogComment({
    onError,
    onCompleted,
    onBeforeCall
  });

  const { addConsultantEventLogCall } = useAddConsultantEventLogCall({
    onError,
    onCompleted,
    onBeforeCall
  });

  const { data: personalInfoData } = useGetPersonalInfoCardDataQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: props.consultantId
    }
  });

  const onSubmit = () => {
    switch (props.activeInput) {
      case EventLogTextType.Comment:
        return addConsultantEventLogComment({
          consultantId: props.consultantId,
          desc: inputText
        });

      case EventLogTextType.Call: {
        const leadStatus = personalInfoData?.consultantById?.leadStatus?.status;

        if (
          props.bookingStatus === ConsultantBookingStatus.NeverBooked &&
          leadStatus !== ConsultantLeadStatus.Assigned
        ) {
          setLeadStatusModalOpen(true);
        } else {
          addConsultantEventLogCall({
            consultantId: props.consultantId,
            desc: inputText
          });
        }

        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    nav.lockNavigation(
      "consultant_profile",
      inputText.length > 0,
      locale.NoteUnSaveBlockMessage
    );
  }, [nav, inputText]);

  if (!props.activeInput) {
    return (
      <Tooltip title={locale.chooseEventLogAction}>
        <TextField
          fullWidth={true}
          placeholder={locale.TypeHere}
          disabled={true}
          InputProps={{ readOnly: true }}
        />
      </Tooltip>
    );
  }

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <TextField
        fullWidth={true}
        multiline={true}
        inputProps={{ rows: 6 }}
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        placeholder={locale.TypeHere}
        disabled={!props.activeInput}
        autoFocus={true}
      />
      <InputButtons
        consultantId={props.consultantId}
        inputType={props.activeInput}
        textAreaContent={inputText}
        setTextAreaContent={setInputText}
        onSubmit={onSubmit}
        isLoading={props.loading}
        setLoading={props.setLoading}
        inputDisabled={props.inputDisabled}
        onSuccess={onCompleted}
      />
      <LeadStatusModal
        consultantId={props.consultantId}
        type={"addCallEvent"}
        open={leadStatusModalOpen}
        setOpen={setLeadStatusModalOpen}
        onSubmit={() =>
          addConsultantEventLogCall({
            consultantId: props.consultantId,
            desc: inputText
          })
        }
      />
    </Box>
  );
};
